'use client';

import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BasicTextarea } from '@/app/components/shared/BasicTextarea';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { toast } from '@/app/components/ui/use-toast';
import { ONE_CLICK_MV_V2_MAX_IMAGE_COUNT } from '@/app/constants';
import { useFetchRecreateData } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  AudioUploadFile,
  FormAudioPrompt,
  FormImagePrompt,
  FormSubtitlePrompt,
  ImageUploadFile,
  OneClickMvScene,
  OneClickMvVersion,
  TaskInfo,
  UploadFile,
} from '@/app/types';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { MAX_REFERENCE_IMAGE_COUNT, MultiImageUpload } from '../_components/MultiImageUpload';
import { ToolAudioUpload } from '../_components/ToolAudioUpload';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { useRunTempUploadBatch } from '../_hooks/use-run-temp-upload-batch';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  applyToolFormParamsPatch,
  buildToolTaskRecreatePatch,
  createPromptListHelpers,
  createToolAudioPromptFromUploadFile,
  createToolImagePromptFromUploadFile,
  createToolSubtitlePromptFromUploadFile,
} from '../_utils';
import {
  getToolTempAudioPendingItems,
  getToolTempImagesPendingItems,
  getToolTempSubtitlePendingItems,
} from '../_utils/tool-temp-upload-items';
import { OneClickMvModeSelector } from './_components/OneClickMvModeSelector';
import {
  type OneClickMvAspectRatio,
  type OneClickMvResolution,
  OneClickMvSettings,
} from './_components/OneClickMvSettings';

const MAX_AUDIO_FILE_SIZE = 20 * 1024 * 1024;
const MAX_SUBTITLE_FILE_SIZE = 30 * 1024;
const MIN_AUDIO_DURATION = 10;
const V1_MAX_AUDIO_DURATION = 180;
const V2_MAX_AUDIO_DURATION = 300;

const OneClickMvForm: FC = () => {
  const t = useTranslations();
  const storeApi = useStoreApi();
  const { settings, updateSettings } = useToolForm('music_video');

  const {
    updateOneClickMvFormParams,
    imagePrompts,
    referenceImagePrompts,
    audioPrompts,
    textPrompt,
    subtitlePrompts,
    rawVersion,
    version,
    scene,
    subtitleEnabled,
    userInfo,
  } = useStore((state) => ({
    updateOneClickMvFormParams: state.updateOneClickMvFormParams,
    imagePrompts: state.oneClickMvFormParams.imagePrompts,
    referenceImagePrompts: state.oneClickMvFormParams.referenceImagePrompts ?? [],
    audioPrompts: state.oneClickMvFormParams.audioPrompts,
    textPrompt: state.oneClickMvFormParams.textPrompt || '',
    subtitlePrompts: state.oneClickMvFormParams.subtitlePrompts ?? [],
    rawVersion: state.oneClickMvFormParams.oneClickMvVersion,
    version: state.oneClickMvFormParams.oneClickMvVersion ?? 'v2',
    scene: state.oneClickMvFormParams.oneClickMvScene ?? 'sing',
    subtitleEnabled: state.oneClickMvFormParams.oneClickMvSubtitleEnabled ?? false,
    userInfo: state.userInfo,
  }));

  const maxImageCount = version === 'v2' ? ONE_CLICK_MV_V2_MAX_IMAGE_COUNT : MAX_REFERENCE_IMAGE_COUNT;
  const maxAudioDuration = version === 'v2' ? V2_MAX_AUDIO_DURATION : V1_MAX_AUDIO_DURATION;

  useEffect(() => {
    if (rawVersion !== undefined) return;
    updateOneClickMvFormParams('oneClickMvVersion', 'v2');
  }, [rawVersion, updateOneClickMvFormParams]);

  const handleTaskSuccess = useCallback(
    async (task: TaskInfo, inspirationId?: string, source?: string, recreateTaskId?: string) => {
      const taskWithFallbackId = !task.id && recreateTaskId ? { ...task, id: recreateTaskId } : task;
      const patch = await buildToolTaskRecreatePatch({
        tool: 'music_video',
        task: taskWithFallbackId,
        baseSettings: settings,
        inspirationId,
        inspirationFrom: source,
      });
      applyToolFormParamsPatch(updateOneClickMvFormParams, patch);
    },
    [settings, updateOneClickMvFormParams],
  );

  useFetchRecreateData({
    onTaskSuccess: handleTaskSuccess,
  });

  useEffect(() => {
    if (imagePrompts.length > maxImageCount) {
      updateOneClickMvFormParams('imagePrompts', imagePrompts.slice(0, maxImageCount));
    }
  }, [imagePrompts, maxImageCount, updateOneClickMvFormParams]);

  useEffect(() => {
    const audioDuration = audioPrompts[0]?.prompt?.duration;
    if (typeof audioDuration === 'number' && audioDuration > maxAudioDuration) {
      updateOneClickMvFormParams('audioPrompts', []);
    }
  }, [audioPrompts, maxAudioDuration, updateOneClickMvFormParams]);

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().oneClickMvFormParams.imagePrompts,
          (prompts) => updateOneClickMvFormParams('imagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts },
      ),
    [imagePrompts, updateOneClickMvFormParams, userInfo],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp role reference images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().oneClickMvFormParams.referenceImagePrompts ?? [],
          (prompts) =>
            updateOneClickMvFormParams('referenceImagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts: referenceImagePrompts },
      ),
    [referenceImagePrompts, updateOneClickMvFormParams, userInfo],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp audio',
    (refs) =>
      getToolTempAudioPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().oneClickMvFormParams.audioPrompts,
          (prompts) => updateOneClickMvFormParams('audioPrompts', prompts.filter(Boolean) as FormAudioPrompt[]),
        ),
        { audioPrompts },
      ),
    [audioPrompts, updateOneClickMvFormParams, userInfo],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp subtitle',
    (refs) =>
      getToolTempSubtitlePendingItems(
        refs,
        createPromptListHelpers(
          () => (storeApi.getState().oneClickMvFormParams.subtitlePrompts ?? []) as Array<FormSubtitlePrompt | null>,
          (prompts) => updateOneClickMvFormParams('subtitlePrompts', prompts as FormSubtitlePrompt[]),
        ),
        { subtitlePrompts },
      ),
    [subtitlePrompts, updateOneClickMvFormParams, userInfo],
  );

  const handleRemoveImage = useCallback(
    (fileId: string) => {
      updateOneClickMvFormParams(
        'imagePrompts',
        imagePrompts.filter((item) => item.id !== fileId),
      );
    },
    [imagePrompts, updateOneClickMvFormParams],
  );

  const handleImageUpload = useMemoizedFn((file: ImageUploadFile) => {
    const imagePrompt = createToolImagePromptFromUploadFile(file);
    const currentImagePrompts = storeApi.getState().oneClickMvFormParams.imagePrompts;
    const existingIndex = currentImagePrompts.findIndex((item) => item.id === file.id);

    if (existingIndex !== -1) {
      const nextImagePrompts = [...currentImagePrompts];
      nextImagePrompts[existingIndex] = imagePrompt;
      updateOneClickMvFormParams('imagePrompts', nextImagePrompts);
    } else if (currentImagePrompts.length < maxImageCount) {
      updateOneClickMvFormParams('imagePrompts', [...currentImagePrompts, imagePrompt]);
    }

    if (file.status === 'error' || file.status === 'removed') {
      handleRemoveImage(file.id);
    }
  });

  const handleRemoveReferenceImage = useCallback(
    (fileId: string) => {
      updateOneClickMvFormParams(
        'referenceImagePrompts',
        referenceImagePrompts.filter((item) => item.id !== fileId),
      );
    },
    [referenceImagePrompts, updateOneClickMvFormParams],
  );

  const handleReferenceImageUpload = useMemoizedFn((file: ImageUploadFile) => {
    const imagePrompt = createToolImagePromptFromUploadFile(file);
    const currentPrompts = storeApi.getState().oneClickMvFormParams.referenceImagePrompts ?? [];
    const existingIndex = currentPrompts.findIndex((item) => item.id === file.id);

    if (existingIndex !== -1) {
      const nextPrompts = [...currentPrompts];
      nextPrompts[existingIndex] = imagePrompt;
      updateOneClickMvFormParams('referenceImagePrompts', nextPrompts);
    } else {
      updateOneClickMvFormParams('referenceImagePrompts', [...currentPrompts, imagePrompt]);
    }

    if (file.status === 'error' || file.status === 'removed') {
      handleRemoveReferenceImage(file.id);
    }
  });

  const getSelectedImage = useCallback(async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = URL.createObjectURL(blob);
      });
      return { blob, width: img.width, height: img.height };
    } catch {
      return null;
    }
  }, []);

  const handleRemoveAudio = useCallback(() => {
    updateOneClickMvFormParams('audioPrompts', []);
  }, [updateOneClickMvFormParams]);

  const handleAudioUpload = useMemoizedFn((file: AudioUploadFile) => {
    if (file.status === 'error' || file.status === 'removed') {
      handleRemoveAudio();
      return;
    }

    const audioPrompt = createToolAudioPromptFromUploadFile(file);
    if (typeof file.file?.duration === 'number' && audioPrompt.prompt) {
      audioPrompt.prompt = {
        ...audioPrompt.prompt,
        duration: Math.round(file.file.duration),
      };
    }
    updateOneClickMvFormParams('audioPrompts', [audioPrompt]);

    if (file.status === 'done' && typeof file.file?.duration === 'number') {
      // 上传元数据与任务参数使用同一个整数时长，避免后端校验不一致
      updateSettings({ duration: Math.round(file.file.duration) });
    }
  });

  const handleMediaDrop = useMemoizedFn((file: UploadFile | AudioUploadFile, type: 'image' | 'video' | 'audio') => {
    if (type === 'image') {
      handleImageUpload(file as ImageUploadFile);
    } else if (type === 'audio') {
      handleAudioUpload(file as AudioUploadFile);
    }
  });

  const handleSubtitleFile = useMemoizedFn((file: File) => {
    if (!file.name.toLowerCase().endsWith('.srt')) {
      toast({
        message: t('tools.oneClickMv.subtitle.invalidType'),
        type: 'error',
      });
      return;
    }

    if (file.size > MAX_SUBTITLE_FILE_SIZE) {
      toast({
        message: t('tools.oneClickMv.subtitle.invalidSizeWithLimit', { limit: '30KB' }),
        type: 'error',
      });
      return;
    }

    const id = uuidv4();
    const uploadFile = {
      id,
      status: userInfo ? ('uploading' as const) : ('done' as const),
      file: {
        src: '',
        fileName: file.name,
        _tempFile: file,
      },
    };
    const prompt = createToolSubtitlePromptFromUploadFile(uploadFile);
    const current = storeApi.getState().oneClickMvFormParams.subtitlePrompts ?? [];
    updateOneClickMvFormParams('subtitlePrompts', [...current, prompt]);
  });

  const handleRemoveSubtitle = useCallback(
    (id: string) => {
      const current = storeApi.getState().oneClickMvFormParams.subtitlePrompts ?? [];
      updateOneClickMvFormParams(
        'subtitlePrompts',
        current.filter((item) => item.id !== id),
      );
    },
    [updateOneClickMvFormParams, storeApi],
  );

  const imageList: ImageUploadFile[] = imagePrompts.map((item) => ({
    id: item.id,
    status: item.status,
    file: item.prompt || null,
  })) as ImageUploadFile[];
  const referenceImageList: ImageUploadFile[] = referenceImagePrompts.map((item) => ({
    id: item.id,
    status: item.status,
    file: item.prompt || null,
  })) as ImageUploadFile[];
  const audioPrompt = audioPrompts[0] || null;
  const requiresRoleReference = version === 'v2' && (scene === 'sing' || scene === 'sing_act');
  const remainingImageSlots = Math.max(0, maxImageCount - imagePrompts.length);
  const remainingAudioSlots = audioPrompt ? 0 : 1;
  const hasUploadingImages = imagePrompts.some((item) => item.status === 'uploading');
  const hasUploadingReferenceImages = referenceImagePrompts.some((item) => item.status === 'uploading');
  const hasUploadingAudio = audioPrompts.some((item) => item.status === 'uploading');
  const hasUploadingSubtitle = subtitlePrompts.some((item) => item.status === 'uploading');
  const hasValidImages = imagePrompts.some((item) =>
    version === 'v2'
      ? item.status === 'done' && !!item.prompt?.selectedImage && !!item.prompt.sourceImage
      : item.status === 'done' && !!item.prompt,
  );
  const hasValidReferenceImage = referenceImagePrompts.some(
    (item) => item.status === 'done' && !!item.prompt?.selectedImage && !!item.prompt.sourceImage,
  );
  const hasValidAudio =
    audioPrompt?.status === 'done' &&
    (version === 'v2' ? !!audioPrompt.prompt && (!!audioPrompt.prompt.src || !userInfo) : !!audioPrompt.prompt);
  const disabledTip = useMemo(() => {
    if (
      hasUploadingImages ||
      hasUploadingAudio ||
      (requiresRoleReference && hasUploadingReferenceImages) ||
      (version === 'v1' && hasUploadingSubtitle)
    ) {
      return t('create.createForm.error.uploadingMedia');
    }

    if (!hasValidImages) {
      return t('tools.oneClickMv.submit.disabledMissingImages');
    }

    if (!hasValidAudio) {
      return t('tools.oneClickMv.submit.disabledMissingAudio');
    }

    if (requiresRoleReference && !hasValidReferenceImage) {
      return t('tools.oneClickMv.submit.disabledMissingRoleReference');
    }

    return '';
  }, [
    hasUploadingAudio,
    hasUploadingImages,
    hasUploadingReferenceImages,
    hasUploadingSubtitle,
    hasValidAudio,
    hasValidImages,
    hasValidReferenceImage,
    requiresRoleReference,
    t,
    version,
  ]);

  return (
    <ToolFormContainer
      className="bg-[#0a0d10] px-5 py-10"
      title={t('tools.oneClickMv.title')}
      description={t('tools.oneClickMv.description')}
      exampleSection={<ExampleMediaPreview tool="music_video" />}
      mediaSection={
        <MediaDropZone
          className="flex flex-col gap-5"
          maxImageCount={remainingImageSlots}
          disabled={remainingImageSlots === 0 && remainingAudioSlots === 0}
          getSelectedImage={getSelectedImage}
          allowTempMode={true}
          acceptAudio={true}
          maxAudioCount={remainingAudioSlots}
          maxAudioDuration={maxAudioDuration}
          minAudioDuration={MIN_AUDIO_DURATION}
          maxAudioSize={MAX_AUDIO_FILE_SIZE}
          extraFileAccept={
            version === 'v1'
              ? {
                  accept: ['.srt', 'application/x-subrip'],
                  onFile: handleSubtitleFile,
                }
              : undefined
          }
          onChange={handleMediaDrop}
        >
          <OneClickMvModeSelector
            version={version}
            scene={scene}
            onVersionChange={(value: OneClickMvVersion) => updateOneClickMvFormParams('oneClickMvVersion', value)}
            onSceneChange={(value: OneClickMvScene) => updateOneClickMvFormParams('oneClickMvScene', value)}
          />

          {requiresRoleReference ? (
            <div className="space-y-2">
              <MultiImageUpload
                imageList={referenceImageList}
                onImageUpload={handleReferenceImageUpload}
                onRemoveImage={handleRemoveReferenceImage}
                getSelectedImage={getSelectedImage}
                title={t('tools.oneClickMv.roleReference.label')}
                maxCount={2}
                imageAccept={['image/jpeg', 'image/png', 'image/bmp', 'image/webp']}
                showOrder={true}
                enableDrop={true}
                className="space-y-3"
              />
              <div className="text-system-white/40 text-xs leading-[1.4]">
                {t('tools.oneClickMv.roleReference.helper')}
              </div>
            </div>
          ) : null}

          <div className="space-y-2">
            <MultiImageUpload
              imageList={imageList}
              onImageUpload={handleImageUpload}
              onRemoveImage={handleRemoveImage}
              getSelectedImage={getSelectedImage}
              maxCount={maxImageCount}
              className="space-y-3"
            />
            <div className="text-system-white/40 text-xs leading-[1.4]">
              {t('tools.oneClickMv.imageUpload.helper', { count: maxImageCount })}
            </div>
          </div>

          <ToolAudioUpload
            audioPrompt={audioPrompt}
            onAudioUpload={handleAudioUpload}
            onRemoveAudio={handleRemoveAudio}
            label={t('tools.oneClickMv.audioUpload.label')}
            helperText={t('tools.oneClickMv.audioUpload.helper', { maxDuration: maxAudioDuration })}
            minDuration={MIN_AUDIO_DURATION}
            maxDuration={maxAudioDuration}
            maxSize={MAX_AUDIO_FILE_SIZE}
            normalizeDurationForUpload={Math.round}
            className="gap-3"
          />

          <div className="space-y-3">
            <div className="font-medium text-sm text-system-white leading-[1.4]">
              {t('tools.oneClickMv.prompt.label')}
            </div>
            <div className="relative flex min-h-[120px] w-full flex-col rounded-16 border border-system-white08 bg-system-white04 p-3 backdrop-blur-[30px]">
              <BasicTextarea
                placeholder={t('tools.oneClickMv.prompt.placeholder')}
                textPrompt={textPrompt}
                updateTextPrompt={(value) => updateOneClickMvFormParams('textPrompt', value)}
                showClearButton={!!textPrompt}
                onClearPrompt={() => updateOneClickMvFormParams('textPrompt', '')}
              />
            </div>
          </div>
        </MediaDropZone>
      }
      settingsSection={
        <OneClickMvSettings
          aspectRatio={(settings?.aspect_ratio as OneClickMvAspectRatio) || '16:9'}
          resolution={(settings?.resolution as OneClickMvResolution) || '1080p'}
          version={version}
          subtitleEnabled={subtitleEnabled}
          subtitlePrompts={subtitlePrompts}
          onAspectRatioChange={(value) => updateSettings({ aspect_ratio: value })}
          onResolutionChange={(value) => updateSettings({ resolution: value })}
          onSubtitleFile={handleSubtitleFile}
          onRemoveSubtitle={handleRemoveSubtitle}
          onSubtitleEnabledChange={(enabled) => updateOneClickMvFormParams('oneClickMvSubtitleEnabled', enabled)}
        />
      }
      submitButton={
        <ToolFormSubmitButton taskType="music_video" disabled={!!disabledTip} disabledTip={disabledTip}>
          {t('create.createForm.submit')}
        </ToolFormSubmitButton>
      }
    />
  );
};

export default OneClickMvForm;
